import type { Editor, Extension, JSONContent } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { type Accessor, createEffect, on, onMount, type Component } from "solid-js";
import { isServer, Show } from "solid-js/web";
import { createEditor, EditorContent } from "tiptap-solid";
import { Placeholder } from "@tiptap/extension-placeholder";
import { MentionsExtension, type useMentionsState } from "./extensions/mentions";
import { MentionsPickerPopup } from "./extensions/mentions/MentionsPickerPopup";
import { VariableV1, type VariableV1Props } from "./extensions/variableV1/VariableV1.extension";
import type { RichText } from "@storytell/cp-client";
import { proseMirrorToRichText } from "../converters/proseMirrorToRichText";
import type { PmDoc } from "@storytell/types/richText";
import type { RichTextEditorInstance } from "../types";
import { promptClasses } from "./promptClasses";
import { parseStringIntoJSONContent } from "./helpers/parseStringIntoEditorJSONContent";
import { insertPromptWithAnimation } from "./helpers/insertPromptWithAnimation";

export type PromptEditorInstance = ReturnType<typeof createPromptEditor>;

export const createPromptEditor = (props: {
  autofocus?: () => boolean;
  placeholder?: () => string;
  initialContent?: PmDoc;
  onBeforeCreate?: (editor: RichTextEditorInstance) => void;
  onCreate?: (editor: RichTextEditorInstance) => void;
  onSubmit: (richText: RichText.RichText, text: string) => void;
  onInput: (text: string) => void;
  mentionsState: ReturnType<typeof useMentionsState>;
  variableV1Props: VariableV1Props;
  editable?: boolean;
}) => {
  const editor = isServer
    ? () => null
    : createEditor({
        content: props.initialContent,
        editorProps: {
          editable: () => props.editable ?? true,
          attributes: {
            class: promptClasses.wrapper,
          },
          handleKeyDown: (() => {
            let enableEnter = true;
            return (_view, event) => {
              const isEnter = event.key === "Enter";
              const isShift = event.shiftKey;

              if (isEnter && props.mentionsState.mentionsState.open) {
                return false;
              }

              if (isEnter && isShift) {
                enableEnter = false;
                editor()?.commands.keyboardShortcut("Enter");
                return true;
              }

              if (isEnter) {
                if (enableEnter) {
                  const doc = editor()?.getJSON() as PmDoc | undefined;
                  if (doc) {
                    const richText = proseMirrorToRichText(doc);
                    props.onSubmit(richText, editor()?.getText() ?? "");
                  }
                  return true;
                }
                enableEnter = true;
                return false;
              }
            };
          })(),
        },
        autofocus: props.autofocus?.(),
        extensions: [
          StarterKit.configure({
            heading: false,
            horizontalRule: false,
            code: {
              HTMLAttributes: {
                class: promptClasses.code,
              },
            },
            codeBlock: {
              HTMLAttributes: {
                class: promptClasses.codeBlock,
              },
            },
            blockquote: {
              HTMLAttributes: {
                class: promptClasses.blockquote,
              },
            },
            bulletList: {
              HTMLAttributes: {
                class: promptClasses.bulletList,
              },
            },
            orderedList: {
              HTMLAttributes: {
                class: promptClasses.orderedList,
              },
            },
            italic: {
              HTMLAttributes: {
                class: promptClasses.italic,
              },
            },
            bold: {
              HTMLAttributes: {
                class: promptClasses.bold,
              },
            },
          }) as Extension,
          Placeholder.configure({
            placeholder: () => props.placeholder?.() || "",
            emptyEditorClass:
              "before:content-[attr(data-placeholder)] before:float-left before:h-0 before:text-disabled-text before:pointer-events-none",
          }),
          VariableV1(props.variableV1Props),
          MentionsExtension({
            autocompleteActionsHandler: props.mentionsState.autocompleteActionsHandler,
          }),
        ],
        onBeforeCreate: ({ editor }) => {
          props.onBeforeCreate?.(editor);
        },
        onCreate: ({ editor }) => {
          props.onCreate?.(editor);

          editor.on("update", () => {
            props.onInput(editor.getText());
          });

          if (props.autofocus) {
            editor.commands.focus();
          }
        },
      });

  createEffect(
    on(
      () => props.placeholder?.(),
      () => {
        editor()?.commands.focus();
      },
    ),
  );

  const typePrompt = async (
    content: string | PmDoc,
    opts?: Partial<{
      instant: boolean;
    }>,
  ) => {
    editor()?.commands.setContent("");
    if (opts?.instant) {
      editor()?.commands.setContent(parseStringIntoJSONContent(content));
      return;
    }

    const insertContent = (content: string | JSONContent) => editor()?.commands.insertContent(content);
    const setContent = (content: string | JSONContent) => editor()?.commands.setContent(content);

    await insertPromptWithAnimation(parseStringIntoJSONContent(content), insertContent, setContent);
  };

  const setEditable = (editable: boolean) => {
    editor()?.setOptions({ editable });
  };

  return {
    editor,
    placeholder: () => props.placeholder?.(),
    autofocus: () => props.autofocus?.(),
    mentionsState: props.mentionsState,
    setEditable,
    typePrompt,
  };
};

export const PromptEditor: Component<{
  context: PromptEditorInstance;
}> = (props) => {
  let textareaRef: HTMLTextAreaElement | undefined = undefined;
  onMount(() => {
    if (textareaRef && !props.context.editor()) {
      textareaRef.focus();
    }
  });

  return (
    <Show
      when={props.context.editor()}
      fallback={
        <textarea
          ref={(el) => {
            textareaRef = el;
          }}
          placeholder={props.context.placeholder()}
          class={`${promptClasses.wrapper} m-0 resize-none`}
          rows={1}
          autofocus={props.context.autofocus()}
        />
      }
    >
      <EditorContent class="text-left" editor={props.context.editor()} />

      <MentionsPickerPopup mentionsState={props.context.mentionsState} />
    </Show>
  );
};
